
import { computed, defineComponent, reactive, ref } from 'vue'
import { judgeDiscussion, priorDiscussion, discussionDocument, witness, answer, adversaria } from '@/constants/statuses'
import { AxiosResponse } from 'axios'
import { InlineResponse2001 as ProximitySearchResult } from '@/api'
import { ElForm, ElTable } from 'element-plus'
import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import JrDateRangePicker from '@/components/jr-parts/JrDateRangePicker.vue'
import InputKilo from '@/components/inputs/InputKilo.vue'
import DepartmentSelect from '@/components/inputs/DepartmentSelect.vue'
import LineSelect from '@/components/inputs/LineSelect.vue'
import StatusSelect from '@/components/inputs/StatusSelect.vue'
import StationSelect from '@/components/inputs/StationSelect.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrText from '@/components/jr-parts/JrText.vue'
import { proximityListRule } from '@/constants/rules'
import { ReadListRequest, PrintCreateRequest } from '@/requests/requestHeader'
import { LocationQueryValue, useRoute } from 'vue-router'
import { useStore, Store } from '@/store'
import { ProximitySearchForm } from '@/types/forms'
import {
  proximitySummaryColumnMap as colMap,
  proximitySummaryColumnKeyMap as keyMap,
  getSortColumn,
  colorsCellStyle,
  colorsCellClass,
} from '@/services/list'
import { RequestHeaderList, RequestHeaderSummaryListPrintResponse } from '@/api'
import {
  PROXIMITY_SUMMARY_LIST_PAGINATE_BY,
  PROXIMITY_SUMMARY_LIST_EXPORT_FILE_NAME,
} from '@/constants/backendConstants'
import { fileDownload } from '@/services/file'
import { dateToSimpleString } from '@/services/date'

interface ColumnInfo {
  order: string
  prop: string
}

export default defineComponent({
  name: 'DiscussionList',
  components: {
    JrButton,
    JrText,
    JrDateRangePicker,
    InputKilo,
    DepartmentSelect,
    LineSelect,
    StatusSelect,
    StationSelect,
    UserLoginWrapper,
  },
  setup() {
    const dataCount = ref<number | null>(1)
    const currentPage = ref<number | null>(1)
    const list = reactive<RequestHeaderList[]>([])
    const form = reactive<ProximitySearchForm>({
      applicant_number: '',
      applicant_date_start: '',
      applicant_date_end: '',
      department_id: null,
      department_name: '',
      department_code: '',
      applicant_title: '',
      line_code: '',
      meter_start: null,
      meter_end: null,
      station_id: null,
      judge_discussion_status: '',
      prior_discussion_status: '',
      discussion_document_status: '',
      witness_status: '',
      answer_status: '',
      adversaria_status: '',
      order_by: '',
      page: 1,
    })

    const route = useRoute()
    const store = useStore() as Store
    const elTableRef = ref<InstanceType<typeof ElTable>>()

    const getQueryParam = () => {
      const query = route.query
      if (returnedScreenJudgement(query.screen)) {
        const conditions = store.getters['proximityList/getSearchCondition']
        if (conditions != null) {
          form.applicant_number = conditions.applicant_number
          form.department_id = conditions.department_id
          form.department_name = conditions.department_name
          form.department_code = conditions.department_code
          form.applicant_title = conditions.applicant_title
          form.line_code = conditions.line_code
          form.meter_start = conditions.meter_start
          form.meter_end = conditions.meter_end
          form.station_id = conditions.station_id
          form.judge_discussion_status = conditions.judge_discussion_status
          form.prior_discussion_status = conditions.prior_discussion_status
          form.discussion_document_status = conditions.discussion_document_status
          form.witness_status = conditions.witness_status
          form.answer_status = conditions.answer_status
          form.adversaria_status = conditions.adversaria_status

          if (conditions.applicant_date_start != null && conditions.applicant_date_end != null) {
            form.applicant_date_start = conditions.applicant_date_start
            form.applicant_date_end = conditions.applicant_date_end
          }

          // 申請件名詳細からの遷移の場合
          if (query.screen === 'detail') {
            form.order_by = conditions.order_by
            form.page = conditions.page

            // テーブルソート
            setSortCondition(form.order_by)
          }
        }
      }
    }

    /**
     * 近接協議一覧出力
     */
    const print = async () => {
      const request = new PrintCreateRequest(
        form.applicant_number,
        form.applicant_date_start,
        form.applicant_date_end,
        form.department_id,
        form.applicant_title,
        form.line_code,
        form.meter_start,
        form.meter_end,
        form.station_id,
        form.judge_discussion_status,
        form.prior_discussion_status,
        form.discussion_document_status,
        form.witness_status,
        form.answer_status,
        form.adversaria_status,
        form.order_by,
        form.page
      )

      await request.exec()

      if (request.success) {
        const { data } = request.getResponse() as AxiosResponse<RequestHeaderSummaryListPrintResponse>
        const downloadFileName =
          PROXIMITY_SUMMARY_LIST_EXPORT_FILE_NAME + '_' + dateToSimpleString(new Date()) + '.xlsx'
        fileDownload(data, downloadFileName)
      }
    }

    const search = async (pageReset = false) => {
      if (!validate()) {
        return false
      }
      if (pageReset) {
        form.page = 1
      }

      const request = new ReadListRequest(
        form.applicant_number,
        form.applicant_date_start,
        form.applicant_date_end,
        form.department_id,
        form.applicant_title,
        form.line_code,
        form.meter_start,
        form.meter_end,
        form.station_id,
        form.judge_discussion_status,
        form.prior_discussion_status,
        form.discussion_document_status,
        form.witness_status,
        form.answer_status,
        form.adversaria_status,
        form.order_by,
        form.page
      )

      await request.exec()
      if (request.success) {
        const { data } = request.getResponse() as AxiosResponse<ProximitySearchResult>
        // 総件数を取得
        dataCount.value = data.count

        // ページネーション設定
        currentPage.value = form.page

        // 一覧データ配置
        list.splice(0)
        list.push(...data.results)

        // 検索情報保持
        store.commit('proximityList/setSearchCondition', form)
      }
    }

    // ページネーション変更
    const currentChange = (val: number) => {
      form.page = val
      search()

      // ページを変える時にトップにスクロールを戻す
      if (elTableRef.value) {
        let bodyWrapper = elTableRef.value.$refs.bodyWrapper as Record<keyof HTMLElement, unknown>
        if (typeof bodyWrapper.scrollTop === 'number') {
          bodyWrapper.scrollTop = 0
        }
      }
    }

    // テーブルソート
    const sortChange = (column: ColumnInfo) => {
      let sortBy: string = getSortColumn(column.prop)
      if (column.order === 'descending' && sortBy != '') {
        form.order_by = '-' + sortBy
      } else {
        form.order_by = sortBy
      }
      form.page = 1
      search()
    }
    const sortKey = ref<string | null>(null)
    const orderCondition = ref<string | null>(null)
    const setSortCondition = (order: string | null) => {
      if (!order) {
        sortKey.value = 'applicant_time'
        orderCondition.value = 'descending'
      } else if (order.includes('-')) {
        sortKey.value = order.replace('-', '')
        orderCondition.value = 'descending'
      } else {
        sortKey.value = order
        orderCondition.value = 'ascending'
      }
    }
    // デフォルトソート(申請日時降順)かどうか デフォルト時に降順に赤マークが付与されないため、この判定からクラスを付与しcssで色を付ける
    const isDefaultSort = computed(() => {
      return form.order_by === ''
    })

    const formRef = ref<InstanceType<typeof ElForm>>()
    // バリデーション
    const validate = () => {
      let result = true
      if (formRef.value) {
        formRef.value.validate((valid) => {
          if (!valid) {
            // チェックNG
            result = false
          }
        })
      }
      return result
    }

    // 詳細画面や新規登録画面から返ってきたかを判定
    const returnedScreenJudgement = (screen: LocationQueryValue | LocationQueryValue[]) => {
      if (screen === 'detail' || screen === 'add') {
        return true
      } else {
        return false
      }
    }

    // 検索条件復元
    getQueryParam()
    search()
    return {
      formRef,
      rules: proximityListRule,
      judgeDiscussion,
      priorDiscussion,
      discussionDocument,
      witness,
      answer,
      adversaria,
      search,
      dataCount,
      currentPage,
      list,
      form,
      currentChange,
      getQueryParam,
      sortChange,
      sortKey,
      orderCondition,
      validate,
      PROXIMITY_SUMMARY_LIST_PAGINATE_BY,
      isDefaultSort,
      colorsCellClass,
      colorsCellStyle,
      colMap,
      keyMap,
      print,
      elTableRef,
    }
  },
})
